import React, { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import LocalizedImage from '@/components/common/localized-image'
import { useIntl } from '@/i18n/hooks/use-intl'
import horizontalBackgroundSrc from '@/public/assets/billboard/horizontal-background.png'
import imageEnUrl from '@/public/assets/billboard/image-en.png'
import imageTwUrl from '@/public/assets/billboard/image-tw.png'
import verticalBackgroundSrc from '@/public/assets/billboard/vertical-background.png'

interface BannerSectionProps {
  children: ReactNode
}

const BannerSection: FunctionComponent<BannerSectionProps> = ({ children }) => {
  const { formatMessage } = useIntl()

  return (
    <Background>
      <WidthBoundary>
        <Wrapper>
          <InfoWrapper>
            <Title>
              {formatMessage(
                { id: 'landing:quick_search_title' },
                { br: <br /> },
              )}
            </Title>
            {children}
          </InfoWrapper>
          <ImagePlaceholder>
            <ImageWrapper>
              <LocalizedImage
                fill
                alt='image'
                loading='eager'
                src={imageEnUrl}
                zhHantSrc={imageTwUrl}
              />
            </ImageWrapper>
          </ImagePlaceholder>
        </Wrapper>
      </WidthBoundary>
    </Background>
  )
}

export default BannerSection

const Title = styled.div`
  font-weight: 700;
  font-size: 36px;
  color: ${({ theme }): string => theme.colors.text.primary};

  @media (max-width: 768px) {
    font-size: 24px;
    text-align: center;
  }
`

const Desc = styled.p`
  font-size: 16px;
  max-width: 800px;
  color: ${({ theme }): string => theme.colors.text.primary};

  @media (max-width: 768px) {
    text-align: center;
  }
`

export const InfoWrapper = styled.div`
  padding: 88px 0;

  ${Desc} {
    margin-bottom: 40px;
  }

  @media (max-width: 768px) {
    padding: 0;

    ${Title} {
      margin-bottom: 24px;
    }
  }

  .ant-select-selection-placeholder {
    font-size: 14px;
  }
`
const ImagePlaceholder = styled.div`
  position: relative;
  overflow-y: clip;
`

const ImageWrapper = styled.div`
  position: absolute;
  aspect-ratio: 732 / 570;
  bottom: -24px;
  top: -64px;

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    width: 100%;
  }
`

const Wrapper = styled.div`
  display: flex;

  ${InfoWrapper} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 1px;
    flex: 1 1 auto;
    margin-right: 40px;
    gap: 20px;
    z-index: 1;
  }

  ${ImagePlaceholder} {
    width: 1px;
    flex: 1 1 auto;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding-top: 80px;

    ${InfoWrapper} {
      width: 100%;
      flex: auto;
      margin-right: 0;
    }

    ${ImagePlaceholder} {
      width: 100%;
      flex: auto;
    }
  }
`

const WidthBoundary = styled.div`
  padding-left: 20px;
  padding-right: 20px;
  margin: 0 auto;
  width: 100%;
  max-width: 1120px;
`
const Background = styled.div`
  background: url(${horizontalBackgroundSrc}) no-repeat center center;
  overflow-x: clip;

  @media (max-width: 768px) {
    background: url(${verticalBackgroundSrc}) no-repeat center center;
  }
`
